import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ChevronDown,
  Shield,
  Lock,
  Upload,
  Users,
  History,
  Scale,
  AlertCircle,
  Settings,
  HelpCircle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AudienceFilter = 'all' | 'gestionnaire' | 'employe';

interface FaqItem {
  question: string;
  answer: string;
  audience: 'all' | 'gestionnaire' | 'employe';
}

interface FaqSection {
  id: string;
  icon: React.ReactNode;
  title: string;
  items: FaqItem[];
}

// ---------------------------------------------------------------------------
// Accordion primitives
// ---------------------------------------------------------------------------

function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-neutral-100 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-neutral-50"
      >
        <span className="text-sm font-medium text-neutral-800">{question}</span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-neutral-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-0">
          <div
            className="prose prose-sm max-w-none text-neutral-600"
            dangerouslySetInnerHTML={{ __html: answer }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface VaultHelpContentProps {
  /** Hide the audience filter (useful when embedding in a role-specific page) */
  defaultAudience?: AudienceFilter;
  /** Show the audience filter tabs */
  showAudienceFilter?: boolean;
}

export function VaultHelpContent({
  defaultAudience = 'all',
  showAudienceFilter = true,
}: VaultHelpContentProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [audience, setAudience] = useState<AudienceFilter>(defaultAudience);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (key: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // -------------------------------------------------------------------------
  // FAQ data
  // -------------------------------------------------------------------------

  const sections: FaqSection[] = useMemo(
    () => [
      {
        id: 'getting-started',
        icon: <Shield className="h-5 w-5 text-primary-600" />,
        title: t('vault.help.sections.gettingStarted', 'Premiers pas'),
        items: [
          {
            question: t(
              'vault.help.q.whatIsVault',
              "Qu'est-ce que le Coffre-Fort Numérique ?"
            ),
            answer: t(
              'vault.help.a.whatIsVault',
              "Le Coffre-Fort Numérique est un espace de stockage sécurisé et chiffré permettant de conserver les bulletins de paie et documents RH en toute conformité avec la législation française (Loi El Khomri). Les documents sont chiffrés de bout en bout : seul le propriétaire du coffre peut y accéder."
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.howCreateVault',
              'Comment créer un coffre-fort pour un dossier ?'
            ),
            answer: t(
              'vault.help.a.howCreateVault',
              "Depuis la page d'un dossier, ouvrez le <strong>panneau Coffre-Fort</strong> (icône bouclier). Dans l'onglet <strong>Gestion</strong>, cliquez sur <strong>Activer le Coffre-Fort Dossier</strong>. Renseignez l'email du contact qui recevra l'invitation pour initialiser le coffre."
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.howCreateEmployeeVaults',
              'Comment créer des coffres-forts pour les employés ?'
            ),
            answer: t(
              'vault.help.a.howCreateEmployeeVaults',
              "Dans l'onglet <strong>Gestion</strong> du panneau Coffre-Fort, vous verrez la liste de tous les employés. Cochez les employés souhaités (ou utilisez <strong>Tout sélectionner</strong>), renseignez leur email, puis cliquez sur <strong>Activer les Coffres-Fort Sélectionnés</strong>. Chaque employé recevra une invitation par email."
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.howInitVault',
              'Comment initialiser mon coffre-fort ?'
            ),
            answer: t(
              'vault.help.a.howInitVault',
              "Après avoir reçu l'email d'invitation, cliquez sur le lien pour créer votre compte. Vous devrez ensuite activer l'<strong>authentification à deux facteurs (2FA)</strong>, puis choisir un <strong>mot de passe de chiffrement</strong> pour votre coffre. Ce mot de passe est essentiel : il sert à chiffrer et déchiffrer vos documents. <strong>Personne d'autre ne peut y accéder</strong>, pas même votre employeur."
            ),
            audience: 'employe',
          },
          {
            question: t(
              'vault.help.q.whatStatuses',
              'Que signifient les différents statuts du coffre-fort ?'
            ),
            answer: t(
              'vault.help.a.whatStatuses',
              "<ul><li><strong>En attente d'activation</strong> : le coffre a été créé, l'invitation a été envoyée. L'employé doit créer son compte et initialiser le coffre.</li><li><strong>Actif</strong> : le coffre est opérationnel, les documents peuvent être déposés et consultés.</li><li><strong>Suspendu</strong> : le coffre est temporairement désactivé par le gestionnaire. Les documents sont conservés mais inaccessibles.</li><li><strong>En fermeture</strong> : un préavis de 3 mois est en cours avant la fermeture définitive.</li></ul>"
            ),
            audience: 'all',
          },
        ],
      },
      {
        id: 'security',
        icon: <Lock className="h-5 w-5 text-primary-600" />,
        title: t('vault.help.sections.security', 'Sécurité & Chiffrement'),
        items: [
          {
            question: t(
              'vault.help.q.howEncrypted',
              'Comment mes documents sont-ils protégés ?'
            ),
            answer: t(
              'vault.help.a.howEncrypted',
              "Vos documents sont protégés par un chiffrement <strong>AES-256-GCM</strong>, le standard utilisé par les gouvernements. La clé de chiffrement est dérivée de votre mot de passe via <strong>PBKDF2-SHA256</strong> avec 600 000 itérations (recommandation OWASP). Chaque document a sa propre clé unique. Le serveur ne stocke jamais votre mot de passe ni les clés en clair : c'est l'architecture <strong>zero-knowledge</strong>."
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.zeroKnowledge',
              "Qu'est-ce que l'architecture zero-knowledge ?"
            ),
            answer: t(
              'vault.help.a.zeroKnowledge',
              "Cela signifie que le serveur ne connaît <strong>jamais</strong> le contenu de vos documents. Le chiffrement et le déchiffrement se font entièrement dans votre navigateur. Même en cas de piratage du serveur, vos documents restent illisibles sans votre mot de passe."
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.employerAccess',
              'Mon employeur peut-il voir mes documents ?'
            ),
            answer: t(
              'vault.help.a.employerAccess',
              "<strong>Non.</strong> Votre employeur (gestionnaire) a le rôle <strong>CREATOR</strong> : il peut déposer des documents et voir les métadonnées (nom du fichier, date, taille), mais il ne peut <strong>jamais</strong> accéder au contenu chiffré. Seul vous, en tant que propriétaire (<strong>OWNER</strong>), pouvez déchiffrer et lire vos documents."
            ),
            audience: 'employe',
          },
          {
            question: t(
              'vault.help.q.2fa',
              "Pourquoi l'authentification à deux facteurs est-elle obligatoire ?"
            ),
            answer: t(
              'vault.help.a.2fa',
              "La 2FA ajoute une couche de sécurité : même si quelqu'un obtient votre mot de passe, il ne pourra pas accéder à votre coffre sans votre téléphone. C'est une exigence légale pour la protection des données sensibles (RGPD Art. 32). Utilisez une application comme <strong>Google Authenticator</strong> ou <strong>Authy</strong>."
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.forgotPassword',
              "J'ai oublié mon mot de passe de coffre-fort, que faire ?"
            ),
            answer: t(
              'vault.help.a.forgotPassword',
              "Le mot de passe du coffre-fort est différent de votre mot de passe de connexion. En raison de l'architecture zero-knowledge, <strong>personne ne peut récupérer votre mot de passe de chiffrement</strong>. Si vous l'avez oublié, contactez votre gestionnaire pour réinitialiser votre coffre. <strong>Attention</strong> : une réinitialisation entraînera la perte d'accès aux documents précédemment chiffrés."
            ),
            audience: 'employe',
          },
          {
            question: t(
              'vault.help.q.hybridEncryption',
              'Comment fonctionne le dépôt automatique sans accès au contenu ?'
            ),
            answer: t(
              'vault.help.a.hybridEncryption',
              "Le système utilise un <strong>chiffrement hybride RSA/AES</strong>. Lors de l'initialisation, une paire de clés RSA est générée. La clé publique est stockée sur le serveur et permet de chiffrer les documents déposés automatiquement. Seule votre clé privée (protégée par votre mot de passe) peut les déchiffrer. Ainsi, le gestionnaire peut déposer des bulletins sans jamais voir leur contenu."
            ),
            audience: 'all',
          },
        ],
      },
      {
        id: 'deposits',
        icon: <Upload className="h-5 w-5 text-primary-600" />,
        title: t('vault.help.sections.deposits', 'Dépôt de documents'),
        items: [
          {
            question: t(
              'vault.help.q.autoSync',
              "Qu'est-ce que le dépôt automatique ?"
            ),
            answer: t(
              'vault.help.a.autoSync',
              "Le dépôt automatique récupère chaque mois les bulletins de paie et les DSN depuis l'API OpenPaye et les dépose dans les coffres-forts. Vous pouvez le configurer dans l'onglet <strong>Dépôt Auto</strong> du panneau Coffre-Fort : choisissez les types de documents, le jour et l'heure de synchronisation, et si les documents vont vers le coffre dossier, les coffres employés, ou les deux."
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.manualSync',
              'Comment faire un dépôt manuel depuis OpenPaye ?'
            ),
            answer: t(
              'vault.help.a.manualSync',
              "Dans l'onglet <strong>Dépôt Manuel</strong>, sélectionnez la source <strong>API OpenPaye</strong>. Choisissez les types de documents (bulletins, DSN), la période (mois/année), et le mode de dépôt :<ul><li><strong>Groupé</strong> : les documents sont déposés dans le coffre du dossier (employeur). Tous les bulletins sont aussi déposés dans les coffres employés s'ils existent.</li><li><strong>Individuel</strong> : sélectionnez les employés spécifiques dont vous voulez déposer les bulletins.</li></ul>"
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.localUpload',
              'Comment déposer un fichier local ?'
            ),
            answer: t(
              'vault.help.a.localUpload',
              "Dans l'onglet <strong>Dépôt Manuel</strong>, sélectionnez la source <strong>Fichier Local</strong>. Choisissez l'employé, le type de document, la période, puis glissez-déposez votre fichier ou parcourez vos fichiers. Formats acceptés : PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (max 50 Mo)."
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.depositWithoutVault',
              "Que se passe-t-il si un employé n'a pas de coffre individuel ?"
            ),
            answer: t(
              'vault.help.a.depositWithoutVault',
              "En mode <strong>groupé</strong>, les bulletins de tous les employés sont récupérés et déposés dans le <strong>coffre employeur</strong> (dossier), même si certains employés n'ont pas de coffre individuel. Seuls ceux qui ont un coffre actif recevront en plus une copie dans leur coffre personnel."
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.whereAreMyDocuments',
              'Où trouver mes bulletins de paie ?'
            ),
            answer: t(
              'vault.help.a.whereAreMyDocuments',
              "Connectez-vous à votre espace, déverrouillez votre coffre-fort avec votre mot de passe, puis consultez l'onglet <strong>Documents</strong>. Vous pouvez filtrer par type (bulletin, attestation...) et par période. Les documents les plus récents apparaissent en premier."
            ),
            audience: 'employe',
          },
          {
            question: t(
              'vault.help.q.documentTypes',
              'Quels types de documents sont gérés ?'
            ),
            answer: t(
              'vault.help.a.documentTypes',
              "<ul><li><strong>Bulletin de paie</strong> : fiches de paie mensuelles</li><li><strong>DSN</strong> : Déclarations Sociales Nominatives</li><li><strong>Contrat de travail</strong> : contrats et avenants</li><li><strong>Attestation</strong> : attestations employeur, certificats</li><li><strong>Édition</strong> : documents comptables et éditions diverses</li><li><strong>Autre</strong> : tout autre document RH</li></ul>"
            ),
            audience: 'all',
          },
        ],
      },
      {
        id: 'roles',
        icon: <Users className="h-5 w-5 text-primary-600" />,
        title: t('vault.help.sections.roles', 'Rôles & Permissions'),
        items: [
          {
            question: t(
              'vault.help.q.rolesExplained',
              'Quels sont les différents rôles ?'
            ),
            answer: t(
              'vault.help.a.rolesExplained',
              "<ul><li><strong>OWNER (Propriétaire)</strong> : l'employé. Il est le seul à pouvoir déchiffrer et lire les documents. Il peut télécharger, consulter l'historique et vérifier l'intégrité des fichiers.</li><li><strong>CREATOR (Gestionnaire)</strong> : l'employeur ou le cabinet comptable. Il peut déposer des documents, configurer la synchronisation, voir les métadonnées et l'historique des dépôts, mais <strong>jamais le contenu</strong> des documents.</li></ul>"
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.whatCanManagerSee',
              'Que peut voir le gestionnaire dans mon coffre ?'
            ),
            answer: t(
              'vault.help.a.whatCanManagerSee',
              "Le gestionnaire peut voir : le <strong>nom des fichiers</strong>, leur <strong>date de dépôt</strong>, leur <strong>taille</strong>, leur <strong>type</strong> (bulletin, attestation...) et la <strong>période</strong> associée. Il ne peut <strong>en aucun cas</strong> ouvrir, lire ou télécharger le contenu des documents chiffrés."
            ),
            audience: 'employe',
          },
          {
            question: t(
              'vault.help.q.inviteOtherManagers',
              "Puis-je donner accès à d'autres gestionnaires ?"
            ),
            answer: t(
              'vault.help.a.inviteOtherManagers',
              "Oui, depuis la gestion des permissions du coffre, vous pouvez inviter d'autres utilisateurs avec des droits spécifiques (lecture des métadonnées, dépôt de documents, gestion des permissions). Chaque accès est tracé dans la piste d'audit."
            ),
            audience: 'gestionnaire',
          },
        ],
      },
      {
        id: 'management',
        icon: <Settings className="h-5 w-5 text-primary-600" />,
        title: t('vault.help.sections.management', 'Gestion des coffres'),
        items: [
          {
            question: t(
              'vault.help.q.suspendVault',
              'Comment suspendre un coffre-fort ?'
            ),
            answer: t(
              'vault.help.a.suspendVault',
              "Depuis la gestion du coffre, vous pouvez le suspendre temporairement. Les documents sont conservés mais l'accès est bloqué. Le propriétaire sera notifié. Vous pouvez réactiver le coffre à tout moment."
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.closeVault',
              'Comment fermer un coffre-fort ?'
            ),
            answer: t(
              'vault.help.a.closeVault',
              "La fermeture suit un processus légal en deux étapes :<ol><li><strong>Préavis de 3 mois</strong> : le coffre passe en statut &laquo; En fermeture &raquo;. Le propriétaire est notifié et peut télécharger tous ses documents.</li><li><strong>Fermeture définitive</strong> : après le préavis, le coffre est fermé. Les documents restent archivés conformément à la loi (50 ans minimum).</li></ol>"
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.resendInvitation',
              "Comment renvoyer une invitation à un employé ?"
            ),
            answer: t(
              'vault.help.a.resendInvitation',
              "Dans l'onglet <strong>Gestion</strong>, trouvez l'employé concerné dans la liste. S'il est en statut &laquo; En attente d'activation &raquo;, un bouton <strong>Renvoyer l'invitation</strong> apparaît. Cliquez dessus pour envoyer un nouvel email. Cela fonctionne aussi pour le coffre employeur via le bouton dans la section coffre-fort dossier."
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.folderVsEmployee',
              "Quelle différence entre coffre dossier et coffre employé ?"
            ),
            answer: t(
              'vault.help.a.folderVsEmployee',
              "<ul><li><strong>Coffre dossier (employeur)</strong> : stocke les documents au niveau de l'entreprise (DSN, éditions, copie des bulletins). Accessible par le contact désigné de l'entreprise.</li><li><strong>Coffre employé</strong> : coffre personnel de chaque salarié. Ne contient que ses propres documents. Accessible uniquement par l'employé.</li></ul>"
            ),
            audience: 'all',
          },
        ],
      },
      {
        id: 'audit',
        icon: <History className="h-5 w-5 text-primary-600" />,
        title: t('vault.help.sections.audit', "Historique & Piste d'audit"),
        items: [
          {
            question: t(
              'vault.help.q.whatIsAudit',
              "Qu'est-ce que la piste d'audit ?"
            ),
            answer: t(
              'vault.help.a.whatIsAudit',
              "Chaque action effectuée sur un coffre-fort est enregistrée de manière <strong>immuable</strong> : dépôt de document, téléchargement, modification de permissions, connexion, etc. Les entrées sont <strong>chaînées cryptographiquement</strong> (comme une blockchain) : toute tentative de modification est détectée automatiquement."
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.viewAudit',
              "Comment consulter l'historique ?"
            ),
            answer: t(
              'vault.help.a.viewAudit',
              "En tant que gestionnaire, ouvrez le panneau Coffre-Fort et consultez l'onglet <strong>Historique</strong> pour voir les dépôts récents. Pour la piste d'audit complète, consultez la page de détail du coffre-fort.<br/><br/>En tant qu'employé, l'onglet <strong>Audit</strong> dans votre coffre affiche toutes les actions avec leur horodatage."
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.exportAudit',
              "Puis-je exporter la piste d'audit ?"
            ),
            answer: t(
              'vault.help.a.exportAudit',
              "Oui, les pistes d'audit peuvent être exportées en format <strong>JSON</strong> ou <strong>CSV</strong> pour archivage ou vérification externe. Cette fonctionnalité est utile pour les audits légaux et les contrôles de conformité."
            ),
            audience: 'gestionnaire',
          },
        ],
      },
      {
        id: 'compliance',
        icon: <Scale className="h-5 w-5 text-primary-600" />,
        title: t('vault.help.sections.compliance', 'Conformité légale'),
        items: [
          {
            question: t(
              'vault.help.q.legalFramework',
              'Quel est le cadre légal du coffre-fort ?'
            ),
            answer: t(
              'vault.help.a.legalFramework',
              "Le coffre-fort s'inscrit dans ce cadre :<ul><li><strong>Code du travail</strong> : remise électronique, disponibilité et récupération des bulletins</li><li><strong>Décret n&deg; 2016-1762</strong> : modalités de conservation et préavis de fermeture</li><li><strong>RGPD</strong> : protection des données personnelles et droits des personnes</li><li><strong>eIDAS</strong> : exigences distinctes applicables à un véritable horodatage qualifié</li></ul>"
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.retention',
              'Combien de temps les documents sont-ils conservés ?'
            ),
            answer: t(
              'vault.help.a.retention',
              "Conformément à l'article D3243-8 du Code du travail, les bulletins de paie sont conservés <strong>50 ans minimum</strong> ou jusqu'à ce que le salarié atteigne <strong>75 ans</strong> (le plus long des deux). Le système calcule automatiquement la date de rétention optimale pour chaque document."
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.timestamp',
              "Quel horodatage le coffre utilise-t-il ?"
            ),
            answer: t(
              'vault.help.a.timestamp',
              "Le coffre conserve un horodatage technique lorsqu'il est disponible. La qualification eIDAS du service et la vérification cryptographique complète du jeton ne sont pas établies dans l'état audité : cet horodatage ne doit donc pas être présenté comme qualifié ni comme une preuve irréfutable."
            ),
            audience: 'all',
          },
          {
            question: t(
              'vault.help.q.gdprRights',
              'Quels sont mes droits sur mes données ?'
            ),
            answer: t(
              'vault.help.a.gdprRights',
              "<ul><li><strong>Droit d'accès</strong> : consultez vos documents lorsque le service est disponible</li><li><strong>Récupération</strong> : le Code du travail exige un export intégral, structuré et sans manipulation complexe</li><li><strong>Droit à l'information</strong> : consultez la documentation et les réserves connues</li><li><strong>Audit</strong> : visualisez les actions effectivement enregistrées sur votre coffre</li></ul>"
            ),
            audience: 'employe',
          },
        ],
      },
      {
        id: 'troubleshooting',
        icon: <AlertCircle className="h-5 w-5 text-primary-600" />,
        title: t('vault.help.sections.troubleshooting', 'Résolution de problèmes'),
        items: [
          {
            question: t(
              'vault.help.q.cantAccessVault',
              "Je ne peux pas accéder à mon coffre-fort"
            ),
            answer: t(
              'vault.help.a.cantAccessVault',
              "Vérifiez les points suivants :<ol><li>Avez-vous bien <strong>initialisé</strong> votre coffre-fort après avoir reçu l'invitation ?</li><li>L'<strong>authentification à deux facteurs (2FA)</strong> est-elle activée ?</li><li>Votre coffre n'est-il pas <strong>suspendu</strong> ? Contactez votre gestionnaire.</li><li>Essayez de vous déconnecter et de vous reconnecter.</li></ol>"
            ),
            audience: 'employe',
          },
          {
            question: t(
              'vault.help.q.syncFailed',
              'La synchronisation a échoué'
            ),
            answer: t(
              'vault.help.a.syncFailed',
              "Causes possibles :<ul><li><strong>Identifiants API</strong> : vérifiez que les identifiants OpenPaye du domaine sont corrects dans les paramètres du domaine.</li><li><strong>Coffre non activé</strong> : le coffre-fort du dossier doit être activé avant de pouvoir déposer des documents.</li><li><strong>Période non disponible</strong> : les bulletins de paie doivent exister dans OpenPaye pour la période sélectionnée.</li><li><strong>Pas de contrat actif</strong> : les employés doivent avoir un contrat actif pour que leurs bulletins soient récupérés.</li></ul>"
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.employeeNoEmail',
              "Un employé n'a pas reçu l'invitation"
            ),
            answer: t(
              'vault.help.a.employeeNoEmail',
              "Vérifiez que l'email est correct dans la fiche employé. Demandez à l'employé de vérifier ses <strong>spams/courriers indésirables</strong>. Vous pouvez <strong>renvoyer l'invitation</strong> depuis l'onglet Gestion : trouvez l'employé en statut &laquo; En attente &raquo; et cliquez sur le bouton de renvoi."
            ),
            audience: 'gestionnaire',
          },
          {
            question: t(
              'vault.help.q.cantDownload',
              'Je ne peux pas télécharger un document'
            ),
            answer: t(
              'vault.help.a.cantDownload',
              "Assurez-vous que votre coffre est <strong>déverrouillé</strong> (vous avez saisi votre mot de passe de chiffrement). Si le téléchargement échoue, vérifiez votre connexion internet et essayez avec un autre navigateur. En cas de problème persistant, contactez le support."
            ),
            audience: 'employe',
          },
          {
            question: t(
              'vault.help.q.lost2fa',
              "J'ai perdu l'accès à mon application 2FA"
            ),
            answer: t(
              'vault.help.a.lost2fa',
              "Contactez votre gestionnaire ou le support technique. Après vérification de votre identité, la 2FA pourra être réinitialisée. Vous devrez ensuite configurer à nouveau votre application d'authentification (Google Authenticator, Authy, etc.)."
            ),
            audience: 'employe',
          },
        ],
      },
    ],
    [t]
  );

  // -------------------------------------------------------------------------
  // Filtering
  // -------------------------------------------------------------------------

  const filteredSections = useMemo(() => {
    const normalizedSearch = search.toLowerCase().trim();

    return sections
      .map((section) => {
        const filteredItems = section.items.filter((item) => {
          const audienceMatch = audience === 'all' || item.audience === 'all' || item.audience === audience;
          if (!audienceMatch) return false;
          if (!normalizedSearch) return true;
          return (
            item.question.toLowerCase().includes(normalizedSearch) ||
            item.answer.toLowerCase().includes(normalizedSearch)
          );
        });
        return { ...section, items: filteredItems };
      })
      .filter((section) => section.items.length > 0);
  }, [sections, search, audience]);

  const totalQuestions = filteredSections.reduce((sum, s) => sum + s.items.length, 0);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Search + Audience filter */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('vault.help.searchPlaceholder', 'Rechercher dans la FAQ...')}
            className="w-full rounded-xl border border-neutral-300 bg-white py-2.5 pl-10 pr-4 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>

        {showAudienceFilter && (
          <div className="flex items-center gap-2">
            {(
              [
                { value: 'all', label: t('vault.help.audience.all', 'Tout') },
                { value: 'gestionnaire', label: t('vault.help.audience.gestionnaire', 'Gestionnaire') },
                { value: 'employe', label: t('vault.help.audience.employe', 'Employé') },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setAudience(opt.value)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  audience === opt.value
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
            <span className="ml-auto text-xs text-neutral-400">
              {totalQuestions} {t('vault.help.questionsCount', 'question(s)')}
            </span>
          </div>
        )}
      </div>

      {/* FAQ sections */}
      {filteredSections.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-neutral-200 bg-neutral-50 py-12 text-center">
          <HelpCircle className="mb-3 h-10 w-10 text-neutral-300" />
          <p className="font-medium text-neutral-600">
            {t('vault.help.noResults', 'Aucun résultat')}
          </p>
          <p className="mt-1 text-sm text-neutral-500">
            {t('vault.help.noResultsHint', 'Essayez avec d\'autres termes de recherche.')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSections.map((section) => (
            <div
              key={section.id}
              className="overflow-hidden rounded-xl border border-neutral-200 bg-white"
            >
              {/* Section header */}
              <div className="flex items-center gap-3 border-b border-neutral-100 bg-neutral-50 px-4 py-3">
                {section.icon}
                <h3 className="text-sm font-semibold text-neutral-800">
                  {section.title}
                </h3>
                <span className="ml-auto rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-medium text-neutral-600">
                  {section.items.length}
                </span>
              </div>

              {/* Items */}
              {section.items.map((item, idx) => {
                const key = `${section.id}-${idx}`;
                return (
                  <AccordionItem
                    key={key}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openItems.has(key)}
                    onToggle={() => toggleItem(key)}
                  />
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
